/**
 * PMO看板「実績集計」の表示用 SSOT。
 *
 * - 事業（business, custom field 185550 の生値）ごとのテーマ色
 * - プレビュー / CSV / PDF の集計単位（エリア）定義
 * - CSV 行生成・印刷用 HTML 生成
 *
 * 事業テーマ色は横断 PMO の Div バッジ色（`CROSS_DIV_PMO_SCOPE.badgeColor`）を踏襲しつつ、
 * Spot Rental（奶黄色）/ エアクロメンズ(#373849) / 全社インフラ(灰) / 社長室(紫) を追加で定義する。
 *
 * **エリアの絞り込み軸は 2 つある**: 事業（business, 185550）と Responsible Div（division, 185536）。
 * 開発ブロックの「PI Div」カードだけが後者で、事業カードと同じ案件を含みうる（別口径なので正しい）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 実績集計の事業テーマ色・集計エリア定義（事業軸 / Responsible Div 軸）・CSV/印刷レポート生成の SSOT
 * @graph-connects none
 */
import type {
  PerformanceAggBusiness,
  PerformanceAggMode,
  PerformanceAggregationResponse,
} from '../types/project.js';

/** 事業（business）1件のテーマ。色はレポートのタイトル帯背景に使う。 */
export interface BusinessTheme {
  /** custom field 185550 の生値（バックエンドの business と完全一致） */
  readonly value: string;
  /** テーマ色（HEX, タイトル帯背景） */
  readonly color: string;
  /** テーマ色の上に乗せる文字色 */
  readonly textColor: string;
}

/** 事業未設定 / 未知の事業に使うフォールバックテーマ（灰）。 */
/** @graph-connects none */
const FALLBACK_THEME: Omit<BusinessTheme, 'value'> = { color: '#9CA3AF', textColor: '#FFFFFF' };

/** 事業生値 → テーマ色マッピング。 */
/** @graph-connects none */
const BUSINESS_THEMES: Readonly<Record<string, Omit<BusinessTheme, 'value'>>> = {
  // aC Div（赤, CROSS_DIV_PMO_SCOPE.ac_div.badgeColor）
  エアクロ: { color: '#DC4040', textColor: '#FFFFFF' },
  // Spot Rental（奶黄色）
  'Spot Rental': { color: '#F5DEA0', textColor: '#7A5B00' },
  // エアクロメンズ（指定色）
  エアクロメンズ: { color: '#373849', textColor: '#FFFFFF' },
  // 全社インフラ（灰）
  全社インフラ: { color: '#6B7280', textColor: '#FFFFFF' },
  // Mall Div（緑, CROSS_DIV_PMO_SCOPE.mall_div.badgeColor）
  エアクロモール: { color: '#50BA52', textColor: '#FFFFFF' },
  // Logi Div / AC PORT（青, CROSS_DIV_PMO_SCOPE.log_div.badgeColor）
  'AC PORT': { color: '#56C3DA', textColor: '#FFFFFF' },
  // 社長室（紫, design のみ）
  社長室: { color: '#7C3AED', textColor: '#FFFFFF' },
  // 経営管理室（茶）
  経営管理室: { color: '#795548', textColor: '#FFFFFF' },
};

/**
 * 事業生値からテーマを取得する（未知の事業は灰のフォールバック）。
 * @graph-connects none
 */
export function getBusinessTheme(value: string): BusinessTheme {
  const theme = BUSINESS_THEMES[value] ?? FALLBACK_THEME;
  return { value, ...theme };
}

/**
 * 集計エリアの絞り込み条件。
 *
 * **事業（business）と Responsible Div（division）は別軸**で、どちらで絞るかを kind で表す。
 * 同じ母票が事業エリアと div エリアの両方に現れることがあるが、重複ではなく別口径の集計。
 * - `{ kind: 'business', values: 'all' }` は全事業（全体集計）
 * - `{ kind: 'division', values }` は母票の Responsible Div（custom field 185536）で絞る
 */
export type AreaFilter =
  | { readonly kind: 'business'; readonly values: readonly string[] | 'all' }
  | { readonly kind: 'division'; readonly values: readonly string[] };

/**
 * 集計エリア定義。プレビュー / CSV / PDF の単位。
 * - エアクロエリアは Spot Rental / エアクロメンズ / 全社インフラ を内包する
 */
export interface AreaDef {
  /** i18n キーサフィックス兼識別子 */
  readonly key: string;
  /** このエリアに含める母票の絞り込み条件 */
  readonly filter: AreaFilter;
}

/** @graph-connects none */
const AIRCLOSET_GROUP = ['エアクロ', 'Spot Rental', 'エアクロメンズ', '全社インフラ'] as const;

/** 実績集計「PI Div」エリアが対象にする Responsible Div(185536) の値。 */
/** @graph-connects none */
export const PI_DIV_DIVISION = 'PI Div';

/**
 * 開発モードの集計エリア。
 *
 * 末尾の `pidiv` だけ**事業ではなく Responsible Div で絞る別口径のカード**。PI Div が担当して
 * いる案件は事業としてはエアクロ / モール / AC PORT に散らばるため、事業カードの案件と重複する。
 * それぞれ問いが違う（「その事業にいくら使ったか」vs「PI Div がいくら出したか」）ので、
 * 合計が他カードと合わないのは正しい状態。
 * @graph-connects none
 */
const DEV_AREAS: readonly AreaDef[] = [
  { key: 'all', filter: { kind: 'business', values: 'all' } },
  { key: 'aircloset', filter: { kind: 'business', values: AIRCLOSET_GROUP } },
  { key: 'mall', filter: { kind: 'business', values: ['エアクロモール'] } },
  { key: 'acport', filter: { kind: 'business', values: ['AC PORT'] } },
  { key: 'pidiv', filter: { kind: 'division', values: [PI_DIV_DIVISION] } },
];

/**
 * デザインモードの集計エリア（社長室を追加）。
 *
 * PI Div カードは開発ブロック専用なので除く。
 * @graph-connects none
 */
const DESIGN_AREAS: readonly AreaDef[] = [
  ...DEV_AREAS.filter((def) => def.key !== 'pidiv'),
  { key: 'president', filter: { kind: 'business', values: ['社長室'] } },
];

/**
 * モードに応じた集計エリア定義を返す。
 * @graph-connects none
 */
export function getAreaDefs(mode: PerformanceAggMode): readonly AreaDef[] {
  return mode === 'design' ? DESIGN_AREAS : DEV_AREAS;
}

/**
 * 集計軸が Responsible Div のエリアかを返す。
 *
 * 画面側がこのエリアを事業カードと**別ブロックに分けて描く**ために使う（軸が違うものを
 * 同じグリッドに混ぜると、合計が足し合わないことに気付けない）。
 * @graph-connects getAreaDefs [calls] モード別エリア定義の取得
 */
export function isDivisionArea(mode: PerformanceAggMode, key: string): boolean {
  return getAreaDefs(mode).some((def) => def.key === key && def.filter.kind === 'division');
}

/** エリアの代表テーマ色（カードの pt 文字色 / hover 背景に使う）。 */
/** @graph-connects none */
const AREA_ACCENT: Readonly<Record<string, string>> = {
  aircloset: '#DC4040',
  mall: '#50BA52',
  acport: '#56C3DA',
  president: '#7C3AED',
  // PI Div は事業ではなく Responsible Div 軸の別口径カード。事業色と被らない黄で区別する。
  pidiv: '#EAB308',
};

/**
 * エリアの代表テーマ色を返す。'all'（全体）は色を持たず、呼び出し側で
 * デフォルトのアクセント色（--color-accent-primary）にフォールバックする。
 * @graph-connects none
 */
export function getAreaAccent(key: string): string | undefined {
  return AREA_ACCENT[key];
}

/** 解決済み事業セクション（テーマ + 集計データ）。 */
export interface ResolvedSection {
  theme: BusinessTheme;
  data: PerformanceAggBusiness;
}

/** 解決済み集計エリア（プレビュー / CSV / PDF の描画単位）。 */
export interface ResolvedArea {
  key: string;
  sections: ResolvedSection[];
  totalPoint: number;
  ticketCount: number;
}

/**
 * 浮動小数ノイズを抑えるため小数2桁に丸める。
 * @graph-connects none
 */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * 事業セクションから、Responsible Div が `values` のいずれかに一致する母票だけを残す。
 * 1 件も残らない事業は undefined を返して呼び出し側で落とす。
 *
 * 階層（事業 → 母票 → 子チケット）は事業エリアと共通のままで、母票だけを絞る。
 * プレビュー / PDF の事業色帯と並びをそのまま使い回すため。
 * @graph-connects none
 */
function filterSectionByDivision(
  data: PerformanceAggBusiness,
  values: readonly string[],
): PerformanceAggBusiness | undefined {
  const parents = data.parents.filter((p) => p.division !== null && values.includes(p.division));
  if (parents.length === 0) return undefined;
  return {
    ...data,
    parents,
    totalPoint: round2(parents.reduce((sum, p) => sum + p.totalPoint, 0)),
    ticketCount: parents.reduce((sum, p) => sum + p.children.length, 0),
  };
}

/**
 * レスポンスをエリア定義に従って解決する。データのない事業セクションは除外する。
 * - `business` 軸: 'all' はレスポンスの全事業（pt降順）、それ以外は列挙した事業のみ
 * - `division` 軸: 全事業を走査し、母票の Responsible Div が一致するものだけ残す
 *   （事業エリアと案件が重複しうるが、別口径の集計なので重複排除はしない）
 * @graph-connects none
 */
export function resolveAreas(
  mode: PerformanceAggMode,
  response: PerformanceAggregationResponse,
): ResolvedArea[] {
  const byBusiness = new Map<string, PerformanceAggBusiness>();
  for (const b of response.businesses) byBusiness.set(b.business, b);

  return getAreaDefs(mode).map((def) => {
    let sections: ResolvedSection[];
    if (def.filter.kind === 'division') {
      const values = def.filter.values;
      sections = response.businesses
        .map((data) => filterSectionByDivision(data, values))
        .filter((data): data is PerformanceAggBusiness => data !== undefined)
        .map((data) => ({ theme: getBusinessTheme(data.business), data }));
    } else if (def.filter.values === 'all') {
      sections = response.businesses.map((data) => ({
        theme: getBusinessTheme(data.business),
        data,
      }));
    } else {
      sections = def.filter.values
        .map((value) => byBusiness.get(value))
        .filter((data): data is PerformanceAggBusiness => data !== undefined)
        .map((data) => ({ theme: getBusinessTheme(data.business), data }));
    }

    return {
      key: def.key,
      sections,
      totalPoint: round2(sections.reduce((sum, s) => sum + s.data.totalPoint, 0)),
      ticketCount: sections.reduce((sum, s) => sum + s.data.ticketCount, 0),
    };
  });
}

/**
 * 実績=0 の子チケット・母票・事業を除外した集計エリアを返す（プレビュー / CSV / PDF 用）。
 * - point=0 の子チケットを除外
 * - 残り合計が 0 の母票を除外
 * - 合計が 0 の事業セクションを除外
 * @graph-connects none
 */
export function compactArea(area: ResolvedArea): ResolvedArea {
  const sections = area.sections
    .map((section) => {
      const parents = section.data.parents
        .map((parent) => {
          const children = parent.children.filter((c) => c.point > 0);
          return {
            ...parent,
            children,
            totalPoint: round2(children.reduce((sum, c) => sum + c.point, 0)),
          };
        })
        .filter((parent) => parent.children.length > 0 && parent.totalPoint > 0);
      return {
        theme: section.theme,
        data: {
          ...section.data,
          parents,
          totalPoint: round2(parents.reduce((sum, p) => sum + p.totalPoint, 0)),
          ticketCount: parents.reduce((sum, p) => sum + p.children.length, 0),
        },
      };
    })
    .filter((section) => section.data.parents.length > 0 && section.data.totalPoint > 0);

  return {
    key: area.key,
    sections,
    totalPoint: round2(sections.reduce((sum, s) => sum + s.data.totalPoint, 0)),
    ticketCount: sections.reduce((sum, s) => sum + s.data.ticketCount, 0),
  };
}

/** CSV / 印刷レポートのメタ情報。 */
export interface ReportMeta {
  /** モード表示名（例: 開発 / デザイン） */
  modeLabel: string;
  /** エリア表示名（例: 開発全体 / エアクロ） */
  areaLabel: string;
  startDate: string;
  endDate: string;
}

/**
 * 完了日（YYYY-MM-DD）を表示用に整形する。
 * @graph-connects none
 */
function formatDate(value: string | null): string {
  return value ?? '-';
}

/** ファイル名に使えない文字を '_' に潰す。 */
/** @graph-connects none */
function safeSegment(value: string): string {
  return value.replace(/[\\/:*?"<>|\s]+/g, '_');
}

/**
 * CSV / PDF のファイル名ベース（拡張子・タイムスタンプなし）を組み立てる。
 * 例: 実績集計_開発_エアクロ_2026-06-01_2026-06-29
 * @graph-connects none
 */
export function reportFileBase(meta: ReportMeta): string {
  return `実績集計_${safeSegment(meta.modeLabel)}_${safeSegment(meta.areaLabel)}_${meta.startDate}_${meta.endDate}`;
}

/**
 * CSV 出力のバリアント。
 * - 'full': 事業 / 母票 / 子チケット明細のフラット行（従来）
 * - 'parentOnly': 子チケット明細を出さず、母票単位の pt 明細のみ
 */
export type CsvVariant = 'full' | 'parentOnly';

/**
 * 集計エリアを CSV 行（二次元配列）へ変換する。
 * 先頭にメタ情報、続いて明細行を出力する。
 * - variant='full': 事業 / 母票 / 子チケット明細のフラット行
 * - variant='parentOnly': full から子チケット列を除いた、母票単位の pt 明細のみ
 * @graph-connects none
 */
export function buildAreaCsvRows(
  area: ResolvedArea,
  meta: ReportMeta,
  variant: CsvVariant = 'full',
): (string | number)[][] {
  // 実績=0 は出力しない
  const compact = compactArea(area);
  const rows: (string | number)[][] = [
    ['実績集計レポート'],
    ['区分', meta.areaLabel],
    ['モード', meta.modeLabel],
    ['期間', `${meta.startDate} 〜 ${meta.endDate}`],
    ['合計pt', compact.totalPoint],
    ['チケット数', compact.ticketCount],
    [],
    variant === 'parentOnly'
      ? ['事業', '親チケキー', '親チケ名', 'ポイント']
      : [
          '事業',
          '親チケキー',
          '親チケ名',
          '子チケットキー',
          '子チケット名',
          '種別',
          '担当',
          'ポイント',
          '完了日',
        ],
  ];

  for (const section of compact.sections) {
    for (const parent of section.data.parents) {
      if (variant === 'parentOnly') {
        rows.push([
          section.data.business,
          parent.parentIssueKey,
          parent.parentSummary ?? '',
          parent.totalPoint,
        ]);
        continue;
      }
      for (const child of parent.children) {
        rows.push([
          section.data.business,
          parent.parentIssueKey,
          parent.parentSummary ?? '',
          child.issueKey,
          child.summary,
          child.issueTypeName,
          child.assignee ?? '',
          child.point,
          formatDate(child.completedDate),
        ]);
      }
    }
  }

  return rows;
}

/**
 * HTML 文字列へ埋め込むユーザー入力をエスケープする。
 * @graph-connects none
 */
function escapeHtml(value: string | null | undefined): string {
  if (value === null || value === undefined) return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * 数値ptを表示用に整形（整数は整数、端数は小数1桁）。
 * 整数部が4桁以上（1000以上）のとき千分位カンマを付ける（例: 9999 → 9,999）。
 * @graph-connects none
 */
export function formatPoint(value: number): string {
  const base = Number.isInteger(value) ? String(value) : value.toFixed(1);
  const [intPart, fracPart] = base.split('.');
  const sign = intPart.startsWith('-') ? '-' : '';
  const digits = sign ? intPart.slice(1) : intPart;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fracPart === undefined ? `${sign}${grouped}` : `${sign}${grouped}.${fracPart}`;
}

/** 印刷レポートのライト / ダーク配色。 */
interface ReportPalette {
  pageBg: string;
  cardBg: string;
  border: string;
  h1: string;
  meta: string;
  grand: string;
  key: string;
  summary: string;
  pt: string;
  empty: string;
}

/** @graph-connects none */
const REPORT_PALETTE: Readonly<Record<'light' | 'dark', ReportPalette>> = {
  dark: {
    pageBg: '#0d1017',
    cardBg: '#161922',
    border: '#262b36',
    h1: '#f3f4f6',
    meta: '#9ca3af',
    grand: '#f3f4f6',
    key: '#818cf8',
    summary: '#e5e7eb',
    pt: '#f3f4f6',
    empty: '#9ca3af',
  },
  light: {
    pageBg: '#f8fafc',
    cardBg: '#ffffff',
    border: '#e5e7eb',
    h1: '#111827',
    meta: '#6b7280',
    grand: '#111827',
    key: '#2563eb',
    summary: '#1f2937',
    pt: '#111827',
    empty: '#6b7280',
  },
};

/**
 * 集計エリアを印刷用 HTML 文書文字列へ変換する。
 *
 * - 実績=0 は除外（compactArea）
 * - 子チケット明細は出さず、親チケ単位の pt と各事業の小計・全体合計のみ（PDF 専用要件）
 * - プレビューと揃えた配色（ライト/ダーク両対応）+ 事業テーマ色の帯。印刷時も色が出るよう print-color-adjust:exact
 * - 明細は1ページ目から流れるよう、ページ分割回避は事業ブロック全体ではなく親チケ行のみに付ける
 * @graph-connects none
 */
function buildAreaReportHtml(
  area: ResolvedArea,
  meta: ReportMeta,
  opts: { light: boolean; title: string },
): string {
  const compact = compactArea(area);
  const c = REPORT_PALETTE[opts.light ? 'light' : 'dark'];
  const sectionsHtml = compact.sections
    .map((section) => {
      const parentsHtml = section.data.parents
        .map(
          (parent) => `
            <div class="parent">
              <span class="parent-key">${escapeHtml(parent.parentIssueKey)}</span>
              <span class="parent-summary">${escapeHtml(parent.parentSummary)}</span>
              <span class="parent-pt">${formatPoint(parent.totalPoint)} pt</span>
            </div>`,
        )
        .join('');

      return `
        <section class="business">
          <div class="business-title" style="background:${section.theme.color};color:${section.theme.textColor};">
            <span>${escapeHtml(section.data.business)}</span>
            <span class="business-pt">${formatPoint(section.data.totalPoint)} pt（${String(section.data.parents.length)} 件）</span>
          </div>
          <div class="parents">${parentsHtml}</div>
        </section>`;
    })
    .join('');

  return `<!doctype html>
<html lang="ja">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(opts.title)}</title>
<style>
  * { box-sizing: border-box; }
  html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: -apple-system, "Hiragino Sans", "Noto Sans JP", sans-serif;
    background: ${c.pageBg}; color: ${c.summary}; margin: 24px; }
  h1 { font-size: 20px; margin: 0 0 4px; color: ${c.h1}; }
  .meta { color: ${c.meta}; font-size: 12px; margin-bottom: 6px; }
  .grand { font-size: 15px; font-weight: 700; margin-bottom: 16px; color: ${c.grand}; }
  .business { margin-bottom: 14px; background: ${c.cardBg};
    border: 1px solid ${c.border}; border-radius: 10px; }
  .business-title { display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px; font-weight: 700; font-size: 15px; border-radius: 9px 9px 0 0; }
  .business-pt { font-size: 13px; font-weight: 600; }
  .parents { padding: 4px 14px 10px; }
  .parent { display: flex; gap: 10px; align-items: baseline; padding: 7px 2px;
    border-bottom: 1px solid ${c.border}; page-break-inside: avoid; }
  .parent:last-child { border-bottom: none; }
  .parent-key { font-weight: 600; color: ${c.key}; font-size: 12px; white-space: nowrap; }
  .parent-summary { flex: 1; font-size: 13px; color: ${c.summary}; }
  .parent-pt { font-weight: 700; font-size: 14px; white-space: nowrap; color: ${c.pt}; }
  .empty { color: ${c.empty}; font-size: 13px; }
  @media print { body { margin: 0; padding: 14px; } }
</style>
</head>
<body onload="window.focus();window.print();">
  <h1>${escapeHtml(meta.areaLabel)} 実績集計（${escapeHtml(meta.modeLabel)}）</h1>
  <div class="meta">期間: ${escapeHtml(meta.startDate)} 〜 ${escapeHtml(meta.endDate)}</div>
  <div class="grand">合計 ${formatPoint(compact.totalPoint)} pt / ${String(compact.ticketCount)} 件</div>
  ${sectionsHtml || '<p class="empty">対象期間に完了した実績はありません。</p>'}
</body>
</html>`;
}

/**
 * 集計エリアを新規ウィンドウで開き、ブラウザの印刷ダイアログ（PDF出力）を起動する。
 *
 * - アプリの現在テーマ（data-theme='light' / それ以外=dark）に合わせて配色を切り替える
 * - 文書 title を `titleBase` にして、ブラウザの「PDF として保存」既定ファイル名へ反映する
 * - 自前の HTML 文書を Blob URL で開くためアプリ本体の CSS には影響しない。
 *   印刷トリガーは `<body onload>` に持たせてロード完了との競合を避ける。
 * - popup がブロックされた場合は false を返す。
 * @graph-connects none
 */
export function printAreaReport(area: ResolvedArea, meta: ReportMeta, titleBase?: string): boolean {
  const light =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'light';
  const title = titleBase ?? reportFileBase(meta);
  const url = URL.createObjectURL(
    new Blob([buildAreaReportHtml(area, meta, { light, title })], { type: 'text/html' }),
  );
  const win = window.open(url, '_blank', 'width=1024,height=768');
  if (!win) {
    URL.revokeObjectURL(url);
    return false;
  }
  // 印刷ダイアログ表示後に Blob URL を解放する（早すぎるとロード失敗するため遅延）。
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
  return true;
}
